// Dynamic accounts data based on real consumption data
import { getAllConsumptionData, getTenantsForCSM, getTenantConsumption } from './consumptionData';

// Generate mock company names for tenant IDs
const tenantNames = {
  'tenant_05586': 'TechCorp Solutions',
  'tenant_06928': 'Global Retail Inc',
  'tenant_07180': 'HealthFirst Medical',
  'tenant_12992': 'EduLearn Systems',
  'tenant_20873': 'FinanceHub Pro',
  'tenant_30247': 'CloudScale Networks',
  'tenant_39058': 'DataDrive Analytics',
  'tenant_39252': 'SecureOps Platform',
  'tenant_53031': 'MarketEdge Trading',
  'tenant_53619': 'LogiFlow Transport',
  'tenant_58660': 'GreenEnergy Solar',
  'tenant_61287': 'SmartHome IoT',
  'tenant_63709': 'MediaStream Plus',
  'tenant_64232': 'AutoDrive Motors',
  'tenant_66912': 'FoodChain Supply',
  'tenant_87193': 'BuildRight Construction',
  'tenant_13896': 'Acme Corporation',
  'tenant_19779': 'Blue Ocean Shipping',
  'tenant_90762': 'Mountain Peak Sports'
};

// Generate health score based on data quality metrics
const calculateHealthScore = (tenantId) => {
  const consumption = getTenantConsumption(tenantId);
  if (!consumption) return 75;
  
  const totals = consumption.totalCounts;
  const total = totals.failed + totals.passed + totals.passedWithCautions;
  if (total === 0) return 75;
  
  const failureRate = totals.failed / total;
  const cautionRate = totals.passedWithCautions / total;
  
  // Calculate health score (0-100)
  // Perfect health = 100 (no failures, no cautions)
  // Deduct points for failures (more weight) and cautions (less weight)
  const healthScore = Math.round(100 - (failureRate * 60) - (cautionRate * 20));
  
  return Math.max(0, Math.min(100, healthScore));
};

// Determine status based on health score
const getStatus = (healthScore) => {
  if (healthScore >= 80) return 'healthy';
  if (healthScore >= 60) return 'neutral';
  return 'unhealthy';
};

// Generate account data for a tenant
const generateAccountData = (tenantId, csmEmail) => {
  const healthScore = calculateHealthScore(tenantId);
  const status = getStatus(healthScore);
  const companyName = tenantNames[tenantId] || `Account ${tenantId.split('_')[1]}`;
  const consumption = getTenantConsumption(tenantId);
  
  return {
    id: tenantId,
    name: companyName,
    status: status,
    healthScore: healthScore,
    csm: csmEmail,
    summary: generateSummary(companyName, healthScore, consumption),
    meetings: generateMockMeetings(companyName, healthScore),
    tickets: generateMockTickets(healthScore),
    consumption: generateConsumptionStructure(tenantId),
    contract: generateContract(companyName, csmEmail)
  };
};

// Generate summary based on health score and consumption
const generateSummary = (name, healthScore, consumption) => {
  const totals = consumption.totalCounts;
  const total = totals.failed + totals.passed + totals.passedWithCautions;
  const validationVolume = total.toLocaleString();
  const successRate = total > 0 ? ((totals.passed / total) * 100).toFixed(1) : 0;
  
  if (healthScore >= 80) {
    return `${name} is performing excellently with ${validationVolume} validations processed this period. The team has achieved ${successRate}% clean data pass rate and maintains strong data quality standards.`;
  } else if (healthScore >= 60) {
    return `${name} shows steady performance with ${validationVolume} validations processed. Data quality score is at ${successRate}% with opportunities to enhance validation rules and team processes.`;
  } else {
    return `${name} has processed ${validationVolume} validations this period. Current data quality score of ${successRate}% indicates areas for improvement. Recommended actions include reviewing validation criteria and scheduling team training sessions.`;
  }
};

// Generate mock meetings that reference global actions
const generateMockMeetings = (companyName, healthScore) => {
  const recentDate = new Date();
  const meetings = [];
  
  for (let i = 0; i < 3; i++) {
    const meetingDate = new Date(recentDate);
    meetingDate.setMonth(meetingDate.getMonth() - i);
    
    meetings.push({
      date: meetingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      notes: healthScore >= 80 
        ? `Discussed excellent data quality metrics. Team is happy with the platform performance.`
        : `Addressed data validation concerns. Created action plan for improvement.`,
      // Actions will be filtered from global actions based on accountName
      // This ensures synchronization between main dashboard and profile views
      actionIds: [], // Will be populated by the component based on matching accountName
      transcript: `CSM: Let's review this month's metrics... Client: We've seen ${healthScore >= 80 ? 'great' : 'concerning'} results...`
    });
  }
  
  return meetings;
};

// Generate mock tickets based on health score
const generateMockTickets = (healthScore) => {
  const tickets = [];
  const ticketCount = healthScore >= 80 ? 2 : healthScore >= 60 ? 4 : 6;
  
  for (let i = 0; i < ticketCount; i++) {
    tickets.push({
      id: `TKT-${Math.floor(Math.random() * 9000) + 1000}`,
      priority: healthScore >= 80 ? 'low' : (i < 2 ? 'high' : 'medium'),
      status: i === 0 ? 'In Progress' : 'Pending',
      days: Math.floor(Math.random() * 10) + 1,
      title: healthScore >= 80 
        ? `Feature request: ${['Custom reports', 'API enhancement', 'UI improvement'][i % 3]}`
        : `Data validation issue: ${['Failed validations', 'Performance degradation', 'Integration errors'][i % 3]}`
    });
  }
  
  return tickets;
};

// Generate consumption structure for the tenant
const generateConsumptionStructure = (tenantId) => {
  const consumption = getTenantConsumption(tenantId);
  if (!consumption) return null;
  
  // Get the last 12 months of data
  const months = Object.keys(consumption.monthlyData).sort().slice(-12);
  const monthlyTotals = months.map(month => {
    const data = consumption.monthlyData[month];
    return data.failed + data.passed + data.passedWithCautions;
  });
  
  // Calculate total and create chart structure
  const totalValidations = monthlyTotals.reduce((a, b) => a + b, 0);
  
  return {
    contractedValidations: Math.max(totalValidations * 1.2, 50000), // 20% buffer
    currentUsage: totalValidations,
    dataAssure: consumption, // Include raw data
    yearlyData: {
      'Current Year': {
        labels: months.map(month => {
          const monthNum = month.split('-')[1];
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return monthNames[parseInt(monthNum) - 1];
        }),
        data: monthlyTotals,
        startingValue: 0
      }
    }
  };
};

// Generate contract information
const generateContract = (companyName, csmEmail) => {
  const csmNames = {
    'george@relishiq.com': 'George Michalke',
    'phil@relishiq.com': 'Phil Henderson',
    'ryan@relishiq.com': 'Ryan Thompson',
    'tara@relishiq.com': 'Tara Singh',
    'danielle@relishiq.com': 'Danielle Martinez',
    'andras@relishiq.com': 'Andras Kovacs'
  };
  
  return {
    startDate: 'January 1, 2023',
    duration: '3 years',
    arrValue: '$' + (Math.floor(Math.random() * 900000) + 100000).toLocaleString(),
    currentYear: 2,
    renewalDate: 'January 1, 2026',
    type: 'Enterprise',
    seats: Math.floor(Math.random() * 400) + 100,
    status: 'Active',
    paymentTerms: 'Annual',
    accountManager: csmNames[csmEmail] || 'Account Manager'
  };
};

// Get accounts for a specific CSM
export const getAccountsForCSM = (csmEmail) => {
  const tenantIds = getTenantsForCSM(csmEmail);
  const accounts = {};
  
  tenantIds.forEach(tenantId => {
    accounts[tenantId] = generateAccountData(tenantId, csmEmail);
  });
  
  return accounts;
};

// Get all accounts (for admin view)
export const getAllAccounts = () => {
  const allConsumption = getAllConsumptionData();
  const accounts = {};
  
  Object.keys(allConsumption).forEach(tenantId => {
    const tenantData = allConsumption[tenantId];
    accounts[tenantId] = generateAccountData(tenantId, tenantData.csm);
  });
  
  return accounts;
};